const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const apiUrl = (url) => /^https?:\/\//i.test(url) ? url : `${API_BASE}${url}`;

const state = {
  projectId: Number(localStorage.getItem("miyar.projectId") || 0) || null,
  projects: [],
  summary: null,
  gl: [],
  invoices: [],
  suppliers: [],
  issues: [],
  trail: [],
  config: null,
  pollTimer: null,
};

const $ = (id) => document.getElementById(id);
const nf = new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 });
const money = (value) => `${nf.format(Number(value || 0))} ر.س`;
const percent = (value) => `${Number(value || 0).toFixed(2)}%`;
const dateFmt = (value) => value ? new Date(value).toLocaleDateString("ar-SA") : "—";
const esc = (value) => String(value ?? "").replace(/[&<>'"]/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","'":"&#39;",'"':"&quot;"}[c]));

async function api(url, options = {}) {
  const headers = new Headers(options.headers || {});
  headers.set("X-Actor", $("actor").value.trim() || "reviewer");
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const response = await fetch(apiUrl(url), { ...options, headers });
  if (!response.ok) {
    let detail = `خطأ ${response.status}`;
    try { const data = await response.json(); detail = data.detail || detail; } catch (_) {}
    throw new Error(detail);
  }
  const type = response.headers.get("content-type") || "";
  return type.includes("application/json") ? response.json() : response;
}

function toast(message, isError = false) {
  const el = $("toast");
  el.textContent = message;
  el.style.background = isError ? "var(--danger)" : "var(--navy)";
  el.classList.add("show");
  clearTimeout(el._timer);
  el._timer = setTimeout(() => el.classList.remove("show"), 3000);
}

function requireProject() {
  if (!state.projectId) {
    toast("اختر أو أنشئ مهمة مراجعة أولًا.", true);
    return false;
  }
  return true;
}

async function init() {
  bindUi();
  try {
    state.config = await api("/api/config");
    $("ruleVersion").textContent = `${state.config.version} — تحقق ${state.config.verified_on}`;
    $("professionalNote").textContent = state.config.professional_note;
    await loadProjects();
    if (state.projectId && state.projects.some(p => p.id === state.projectId)) {
      $("projectSelect").value = String(state.projectId);
      await refreshAll();
    } else {
      setProject(null);
    }
  } catch (error) {
    toast(error.message, true);
  }
}

function bindUi() {
  document.querySelectorAll(".tab").forEach(btn => btn.addEventListener("click", () => openView(btn.dataset.view)));
  $("themeBtn").addEventListener("click", () => {
    const next = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    localStorage.setItem("miyar.theme", next);
  });
  document.documentElement.dataset.theme = localStorage.getItem("miyar.theme") || "light";
  $("actor").value = localStorage.getItem("miyar.actor") || "reviewer";
  $("actor").addEventListener("change", () => localStorage.setItem("miyar.actor", $("actor").value));

  $("projectSelect").addEventListener("change", async (e) => {
    setProject(e.target.value ? Number(e.target.value) : null);
    if (state.projectId) await refreshAll();
  });
  $("refreshBtn").addEventListener("click", refreshAll);
  $("openCreateProject").addEventListener("click", () => $("projectDialog").showModal());
  $("projectForm").addEventListener("submit", createProject);

  document.querySelectorAll("[data-upload]").forEach(btn => btn.addEventListener("click", () => uploadTabular(btn.dataset.upload)));
  $("invoiceFiles").addEventListener("change", (e) => {
    const files = [...e.target.files];
    $("invoiceSelection").textContent = files.length ? `${files.length} ملف — ${nf.format(files.reduce((a,f)=>a+f.size,0)/1024/1024)} MB` : "لم يتم اختيار ملفات";
  });
  $("uploadInvoicesBtn").addEventListener("click", uploadInvoices);
  $("runMatchingBtn").addEventListener("click", runMatching);
  $("runCalcBtn").addEventListener("click", runCalculation);

  $("reloadGl").addEventListener("click", loadGl);
  $("glSearch").addEventListener("input", renderGl);
  $("reloadInvoices").addEventListener("click", loadInvoices);
  $("invoiceStatusFilter").addEventListener("change", renderInvoices);
  $("reloadSuppliers").addEventListener("click", loadSuppliers);
  $("reloadIssues").addEventListener("click", loadIssues);
  $("issueFilter").addEventListener("change", loadIssues);
  $("reloadTrail").addEventListener("click", loadTrail);
  $("downloadWp").addEventListener("click", () => downloadReport("working-papers"));
  $("downloadReport").addEventListener("click", () => downloadReport("audit-report"));
}

function openView(id) {
  document.querySelectorAll(".tab").forEach(b => b.classList.toggle("active", b.dataset.view === id));
  document.querySelectorAll(".view").forEach(v => v.classList.toggle("active", v.id === id));
  if (!state.projectId && id !== "dashboard" && id !== "suppliers") toast("اختر مهمة لعرض بياناتها.", true);
  if (id === "gl" && state.projectId) loadGl();
  if (id === "invoices" && state.projectId) loadInvoices();
  if (id === "suppliers") loadSuppliers();
  if (id === "issues" && state.projectId) loadIssues();
  if (id === "trail" && state.projectId) loadTrail();
}

async function loadProjects() {
  state.projects = await api("/api/projects");
  $("projectSelect").innerHTML = `<option value="">اختر مشروعًا</option>` + state.projects.map(p => `<option value="${p.id}">${esc(p.name)}${p.client_name ? ` — ${esc(p.client_name)}` : ""}</option>`).join("");
}

function setProject(id) {
  state.projectId = id;
  localStorage.setItem("miyar.projectId", id || "");
  $("noProject").classList.toggle("hidden", !!id);
  $("dashboardContent").classList.toggle("hidden", !id);
  stopPolling();
}

async function createProject(event) {
  event.preventDefault();
  const submitter = event.submitter;
  if (submitter?.value === "cancel") { $("projectDialog").close(); return; }
  const form = new FormData(event.currentTarget);
  const payload = Object.fromEntries(form.entries());
  for (const key of ["client_name", "period_start", "period_end", "fiscal_year_end"]) if (!payload[key]) payload[key] = null;
  try {
    const project = await api("/api/projects", { method: "POST", body: JSON.stringify(payload) });
    $("projectDialog").close();
    event.currentTarget.reset();
    await loadProjects();
    setProject(project.id);
    $("projectSelect").value = String(project.id);
    await refreshAll();
    toast("تم إنشاء مهمة المراجعة.");
  } catch (error) { toast(error.message, true); }
}

async function refreshAll() {
  if (!requireProject()) return;
  try {
    state.summary = await api(`/api/projects/${state.projectId}/summary`);
    renderDashboard();
    const active = state.summary.jobs?.some(job => ["QUEUED", "RUNNING"].includes(job.status));
    if (active) startPolling(); else stopPolling();
  } catch (error) { toast(error.message, true); }
}

function renderDashboard() {
  const s = state.summary;
  const calc = s.calculation;
  $("kpiConservative").textContent = calc ? percent(calc.conservative_rate) : "—";
  $("kpiResolvedRate").textContent = calc ? percent(calc.resolved_only_rate) : "—";
  $("kpiUnresolved").textContent = calc ? money(calc.unresolved_cost) : "—";
  $("kpiIssues").textContent = s.counts.open_issues;
  $("kpiHighIssues").textContent = `${s.counts.high_issues} عالية الخطورة`;
  $("calcStatus").textContent = calc ? calc.final_status : "لم يُحسب";
  $("countGl").textContent = s.counts.gl;
  $("countInvoices").textContent = s.counts.invoices;
  $("countMatched").textContent = s.counts.matched;
  $("countSuggested").textContent = s.counts.suggested;
  $("countUnmatched").textContent = s.counts.unmatched;
  renderComponents(s.component_totals || {});
  renderJobs(s.jobs || []);
}

function renderComponents(totals) {
  const labels = {workforce:"تعويضات العاملين", goods_services:"السلع والخدمات", capability_development:"تطوير القدرات", depreciation:"الإهلاك", unclassified:"غير مصنف"};
  const max = Math.max(1, ...Object.values(totals));
  $("componentChart").innerHTML = Object.entries(labels).map(([key,label]) => {
    const value = Number(totals[key] || 0);
    return `<div class="bar-row"><span>${label}</span><div class="bar"><i style="width:${value/max*100}%"></i></div><small>${money(value)}</small></div>`;
  }).join("");
}

function renderJobs(jobs) {
  $("jobsList").innerHTML = jobs.length ? jobs.map(job => {
    const pct = job.total_items ? job.processed_items / job.total_items * 100 : 0;
    return `<div class="job"><b>${esc(job.status)}</b><div><div class="progress"><i style="width:${pct}%"></i></div><small class="muted">${job.processed_items}/${job.total_items} — أخطاء ${job.error_count}</small></div><span>${dateFmt(job.created_at)}</span></div>`;
  }).join("") : `<div class="muted">لا توجد عمليات.</div>`;
}

function startPolling() {
  if (state.pollTimer) return;
  state.pollTimer = setInterval(refreshAll, 3500);
}
function stopPolling() { clearInterval(state.pollTimer); state.pollTimer = null; }

async function uploadTabular(type) {
  const map = {
    suppliers: { input: "supplierFile", url: "/api/import/suppliers", label: "الموردين" },
    certificates: { input: "certificateFile", url: "/api/import/certificates", label: "الشهادات" },
    gl: { input: "glFile", url: `/api/projects/${state.projectId}/import/gl`, label: "دفتر الأستاذ", project: true },
  };
  const cfg = map[type];
  if (cfg.project && !requireProject()) return;
  const file = $(cfg.input).files[0];
  if (!file) return toast(`اختر ملف ${cfg.label}.`, true);
  const data = new FormData(); data.append("file", file);
  try {
    const result = await api(cfg.url, { method: "POST", body: data });
    addMessage(`تم استيراد ${cfg.label}: ${JSON.stringify(result)}`);
    toast(`تم استيراد ${cfg.label}.`);
    if (type === "gl") await refreshAll();
  } catch (error) { addMessage(error.message, true); toast(error.message, true); }
}

async function uploadInvoices() {
  if (!requireProject()) return;
  const files = [...$("invoiceFiles").files];
  if (!files.length) return toast("اختر فواتير PDF.", true);
  const data = new FormData(); files.forEach(file => data.append("files", file));
  try {
    const result = await api(`/api/projects/${state.projectId}/invoices/upload`, { method: "POST", body: data });
    addMessage(`بدأت معالجة ${result.files} فاتورة — Job ${result.job_id}`);
    toast("بدأت معالجة الفواتير في الخلفية.");
    $("invoiceFiles").value = ""; $("invoiceSelection").textContent = "لم يتم اختيار ملفات";
    await refreshAll(); startPolling();
  } catch (error) { addMessage(error.message, true); toast(error.message, true); }
}

function addMessage(text, error = false) {
  const div = document.createElement("div");
  div.className = "message"; div.style.borderColor = error ? "var(--danger)" : "var(--primary)"; div.textContent = text;
  $("uploadMessages").prepend(div);
}

async function runMatching() {
  if (!requireProject()) return;
  setBusy($("runMatchingBtn"), true, "جارٍ الربط...");
  try {
    const result = await api(`/api/projects/${state.projectId}/matching/run`, { method: "POST" });
    toast(`الربط: ${result.matched} مؤكد، ${result.suggested} مقترح، ${result.unmatched} غير مربوط.`);
    await refreshAll();
  } catch (error) { toast(error.message, true); }
  finally { setBusy($("runMatchingBtn"), false, "تشغيل الربط"); }
}

async function runCalculation() {
  if (!requireProject()) return;
  setBusy($("runCalcBtn"), true, "جارٍ الحساب...");
  try {
    const result = await api(`/api/projects/${state.projectId}/calculation/run`, { method: "POST" });
    toast(`النسبة المتحفظة ${percent(result.conservative_rate)} — الحالة ${result.final_status}`);
    await refreshAll();
  } catch (error) { toast(error.message, true); }
  finally { setBusy($("runCalcBtn"), false, "تشغيل الحساب"); }
}
function setBusy(button, busy, text) { button.disabled = busy; button.textContent = text; }

async function loadGl() {
  if (!requireProject()) return;
  try { state.gl = await api(`/api/projects/${state.projectId}/gl?limit=2000`); renderGl(); }
  catch (error) { toast(error.message, true); }
}

function renderGl() {
  const q = $("glSearch").value.trim().toLowerCase();
  const rows = state.gl.filter(r => !q || [r.account_code,r.account_name,r.description,r.supplier_name,r.invoice_no].some(v => String(v||"").toLowerCase().includes(q)));
  $("glBody").innerHTML = rows.map(r => `<tr>
    <td>${r.id}</td><td><b>${esc(r.account_code)}</b><br>${esc(r.account_name)}</td><td>${esc(r.description)}</td><td>${esc(r.supplier_name)}<br><small>${esc(r.supplier_vat)}</small></td>
    <td>${esc(r.invoice_no)}</td><td>${money(r.amount)}</td><td>${esc(r.component)}</td><td>${esc(r.locality)}</td><td>${esc(r.sector_code)}</td>
    <td>${r.rate_used == null ? "—" : percent(r.rate_used)}</td><td>${esc(r.rate_source)}</td><td><span class="badge ${r.calculation_status === 'RESOLVED' ? 'resolved' : 'high'}">${esc(r.calculation_status)}</span><br><button class="small-btn" onclick="editGl(${r.id})">تعديل</button></td>
  </tr>`).join("") || `<tr><td colspan="12" class="muted">لا توجد بيانات.</td></tr>`;
}

window.editGl = async function(id) {
  const row = state.gl.find(r => r.id === id); if (!row) return;
  const component = prompt("المكون: workforce / goods_services / capability_development / depreciation / unclassified", row.component || "unclassified");
  if (component === null) return;
  const locality = prompt("المحلية: SAUDI / NON_SAUDI / LOCAL_ASSET / FOREIGN_ASSET_IN_SAUDI / DIRECT_IMPORT أو اتركها فارغة", row.locality || "");
  if (locality === null) return;
  const sector = prompt("كود القطاع 01-16 أو فارغ", row.sector_code || "");
  if (sector === null) return;
  const manualRaw = prompt("نسبة يدوية موثقة 0-100 أو فارغ", row.manual_rate ?? "");
  if (manualRaw === null) return;
  const payload = { component, locality: locality || null, sector_code: sector || null, manual_rate: manualRaw === "" ? null : Number(manualRaw) };
  try { await api(`/api/gl/${id}/classification`, { method: "PATCH", body: JSON.stringify(payload) }); toast("تم تحديث التصنيف."); await loadGl(); }
  catch (error) { toast(error.message, true); }
};

async function loadInvoices() {
  if (!requireProject()) return;
  try { state.invoices = await api(`/api/projects/${state.projectId}/invoices?limit=2000`); renderInvoices(); }
  catch (error) { toast(error.message, true); }
}

function renderInvoices() {
  const filter = $("invoiceStatusFilter").value;
  const rows = state.invoices.filter(r => !filter || r.review_status === filter);
  $("invoiceBody").innerHTML = rows.map(r => `<tr>
    <td>${r.id}</td><td><a class="doc-link" href="${apiUrl(`/api/invoices/${r.id}/file`)}" target="_blank" rel="noopener">${esc(r.file_name)}</a></td><td>${esc(r.supplier_name)}</td><td>${esc(r.supplier_vat)}</td><td>${esc(r.invoice_no)}</td><td>${dateFmt(r.invoice_date)}</td>
    <td>${r.subtotal == null ? "—" : money(r.subtotal)}</td><td>${r.vat_amount == null ? "—" : money(r.vat_amount)}</td><td>${r.total_amount == null ? "—" : money(r.total_amount)}</td><td>${esc(r.extraction_method)}</td>
    <td>${r.extraction_confidence == null ? "—" : percent(r.extraction_confidence*100)}</td><td>${r.gl_transaction_id ?? "—"}</td><td>${r.match_score == null ? "—" : percent(r.match_score*100)}</td>
    <td><span class="badge ${String(r.review_status).toLowerCase()}">${esc(r.review_status)}</span><br><button class="small-btn" onclick="reviewInvoice(${r.id})">اعتماد/ربط</button></td>
  </tr>`).join("") || `<tr><td colspan="14" class="muted">لا توجد فواتير.</td></tr>`;
}

window.reviewInvoice = async function(id) {
  const row = state.invoices.find(r => r.id === id); if (!row) return;
  const glRaw = prompt("GL ID للربط، أو اتركه فارغًا لإلغاء الربط", row.gl_transaction_id ?? "");
  if (glRaw === null) return;
  const status = prompt("حالة المراجعة: REVIEWED / MATCHED / SUGGESTED / UNMATCHED", row.review_status || "REVIEWED");
  if (status === null) return;
  const payload = { supplier_id: row.supplier_id, gl_transaction_id: glRaw === "" ? null : Number(glRaw), component: row.component, sector_code: row.sector_code, review_status: status };
  try { await api(`/api/invoices/${id}/review`, { method: "PATCH", body: JSON.stringify(payload) }); toast("تم تحديث مراجعة الفاتورة."); await loadInvoices(); await refreshAll(); }
  catch (error) { toast(error.message, true); }
};

async function loadSuppliers() {
  try { state.suppliers = await api("/api/suppliers?limit=5000"); renderSuppliers(); }
  catch (error) { toast(error.message, true); }
}
function renderSuppliers() {
  $("supplierBody").innerHTML = state.suppliers.map(s => {
    const c = s.certificate;
    const statusClass = s.certificate_status === "MISSING" ? "high" : s.certificate_status === "EXPIRED" ? "expired" : "valid";
    return `<tr><td>${s.id}</td><td><b>${esc(s.name)}</b></td><td>${esc(s.vat_number)}</td><td>${esc(s.sector_code)}</td><td>${s.reference_rate == null ? "—" : percent(s.reference_rate)}</td><td>${esc(c?.certificate_number)}</td><td>${c ? percent(c.local_content_rate) : "—"}</td><td>${dateFmt(c?.expires_at)}</td><td><span class="badge ${statusClass}">${esc(s.certificate_status)}</span></td></tr>`;
  }).join("") || `<tr><td colspan="9" class="muted">لا يوجد موردون.</td></tr>`;
}

async function loadIssues() {
  if (!requireProject()) return;
  const filter = $("issueFilter").value;
  try { state.issues = await api(`/api/projects/${state.projectId}/issues${filter ? `?status=${filter}` : ""}`); renderIssues(); }
  catch (error) { toast(error.message, true); }
}
function renderIssues() {
  $("issuesCards").innerHTML = state.issues.map(i => `<article class="issue ${String(i.severity).toLowerCase()}"><div class="severity"></div><div><h3><span class="badge ${String(i.severity).toLowerCase()}">${esc(i.severity)}</span> ${esc(i.code)}</h3><p>${esc(i.message)}</p><small class="muted">${esc(i.entity_type)} #${i.entity_id ?? "—"} — ${dateFmt(i.created_at)}</small></div><div class="issue-actions">${i.status === "OPEN" ? `<button class="small-btn" onclick="setIssue(${i.id},'RESOLVED')">إغلاق</button><button class="small-btn" onclick="setIssue(${i.id},'WAIVED')">Waive</button>` : `<span class="badge resolved">${esc(i.status)}</span>`}</div></article>`).join("") || `<div class="empty"><h2>لا توجد ملاحظات في هذا الفلتر.</h2></div>`;
}
window.setIssue = async function(id, status) {
  try { await api(`/api/issues/${id}`, { method: "PATCH", body: JSON.stringify({status}) }); toast("تم تحديث الملاحظة."); await loadIssues(); await refreshAll(); }
  catch (error) { toast(error.message, true); }
};

async function loadTrail() {
  if (!requireProject()) return;
  try { state.trail = await api(`/api/projects/${state.projectId}/audit-trail?limit=3000`); renderTrail(); }
  catch (error) { toast(error.message, true); }
}
function renderTrail() {
  $("trailBody").innerHTML = state.trail.map(e => `<tr><td>${dateFmt(e.created_at)}</td><td>${esc(e.actor)}</td><td>${esc(e.entity_type)}</td><td>${e.entity_id ?? "—"}</td><td><b>${esc(e.action)}</b></td><td><code>${esc(shorten(e.new_value, 220))}</code></td><td>${esc(e.source_ref)}</td></tr>`).join("") || `<tr><td colspan="7" class="muted">لا يوجد سجل.</td></tr>`;
}
function shorten(value, max) { const s = String(value || ""); return s.length > max ? s.slice(0,max) + "…" : s; }

function downloadReport(type) {
  if (!requireProject()) return;
  window.location.href = apiUrl(`/api/projects/${state.projectId}/reports/${type}`);
}

init();
